import * as dataLayer from './getFilteredPeopleDataLayer';
import debounce from 'lodash-es/debounce';
import type { PersonaType } from '../../../service';

const MAX_ENTRIES_TO_DISPLAY = 10;
const FIRST_ENTRIES_SEARCH_COUNT = 30;
const SEARCH_ENTRIES_OFFSET_STEP = 100;
const MAX_ENTRIES_FOR_PDL_SEARCH = 500;
const MAX_SUPPORTED_QUERY_AMOUNT = 150; // Federated API supports a maximum of 150 elements per request

export type SuggestionsFilter<T> = (members: T[]) => T[];
export type ConverterFunction<T> = (personas: PersonaType[]) => T[];
export type AnyValue = any;

export type GetSuggestionsDebounced<T> = (
    filterText: string,
    filterFn: SuggestionsFilter<T>,
    converterFn: ConverterFunction<T>,
    counter: number,
    resolve: AnyValue,
    maxEntriesToDisplay?: number
) => AnyValue;

export function searchPeopleTypeFromSuggestions(
    queryString: string,
    options?: dataLayer.IndexedPageViewExtraOptions
): Promise<PersonaType[]> {
    return dataLayer.getFilteredPeopleTypeFromSuggestions(queryString, options).catch(error => {
        return [];
    });
}

export function searchPeopleTypeFromContacts(
    queryString: string,
    options?: dataLayer.IndexedPageViewExtraOptions
): Promise<PersonaType[]> {
    return dataLayer.getFilteredPeopleTypeFromContacts(queryString, options).catch(error => {
        return [];
    });
}

export function searchPeopleTypeFromFederatedSources(
    queryString: string,
    maxResults: number
): Promise<PersonaType[]> {
    const amountToRequest = Math.min(maxResults, MAX_SUPPORTED_QUERY_AMOUNT);
    return dataLayer
        .getFilteredPeopleTypeFromFederatedSources(queryString, amountToRequest)
        .catch(error => {
            return [];
        });
}

export function getSuggestedPeopleType(
    options?: dataLayer.IndexedPageViewExtraOptions
): Promise<PersonaType[]> {
    return dataLayer.getSuggestedPeopleType(options).catch(error => {
        return [];
    });
}

export const getMemberSuggestionsDebounced: GetSuggestionsDebounced<AnyValue> = debounce(
    (filterText, filterFn, converterFn, currentListMembersCount, resolve, maxEntriesToDisplay?) => {
        return resolve(
            getSearchPersonas(
                filterText,
                filterFn,
                converterFn,
                currentListMembersCount,
                maxEntriesToDisplay || MAX_ENTRIES_TO_DISPLAY
            )
        );
    },
    500
);

// This function combines the logic of searching with 2 APIs as well as filtering and
// converting data from API format to the desired format.
// FilteringFunction will filter the results according to some parameters, and the whole idea behind this complicated logic is:
// It is possible that after filtering, all the results are already displayed, so no data left to show. We want to avoid that.
// That's why the filtering needs to be done in place.
// Another performance trick is:
// We first use fast search from federated sources with a limit of results as small as possible to get results faster.
// Only then we fallback to slow search query which will get all the results.
// This fallback is necessary because fast query might not return N results even if there are N results for the query.
// getSearchPersonasLegacyAPIRecursiveWithFiltering always return all the results.
export async function getSearchPersonas<T>(
    filterText: string,
    filteringFunction: SuggestionsFilter<T>,
    converterFunction: ConverterFunction<T>,
    currentListMembersCount: number,
    maxEntriesToDisplay: number
): Promise<T[]> {
    const results = await searchPeopleTypeFromFederatedSources(
        filterText,
        currentListMembersCount + maxEntriesToDisplay
    );

    const filteredResults = filteringFunction(converterFunction(results));

    return filteredResults.length >= maxEntriesToDisplay
        ? filteredResults
        : getSearchPersonasLegacyAPIRecursiveWithFiltering(
              filterText,
              filteredResults,
              FIRST_ENTRIES_SEARCH_COUNT,
              filteringFunction,
              converterFunction,
              0,
              maxEntriesToDisplay
          );
}

async function getSearchPersonasLegacyAPIRecursiveWithFiltering<T>(
    filterText: string,
    oldResults: T[],
    maxEntries: number,
    filteringFunction: SuggestionsFilter<T>,
    converterFunction: ConverterFunction<T>,
    previousRawResults: number,
    maxEntriesToDisplay: number
): Promise<T[]> {
    const isLastPossibleRequest = maxEntries >= MAX_ENTRIES_FOR_PDL_SEARCH;
    const resultsFromSources = await searchPeopleTypeFromContacts(filterText, {
        MaxEntriesReturned: maxEntries,
    });
    const results = converterFunction(resultsFromSources);
    const noResultsFound = !resultsFromSources.length;
    const newResults = [...oldResults, ...results];
    const filteredResults = filteringFunction(newResults);
    const noNewRawEntriesReturned = resultsFromSources.length === previousRawResults;
    return filteredResults.length >= maxEntriesToDisplay ||
        noResultsFound ||
        isLastPossibleRequest ||
        noNewRawEntriesReturned
        ? filteredResults
        : getSearchPersonasLegacyAPIRecursiveWithFiltering(
              filterText,
              filteredResults,
              maxEntries + SEARCH_ENTRIES_OFFSET_STEP,
              filteringFunction,
              converterFunction,
              resultsFromSources.length,
              maxEntriesToDisplay
          );
}
